import { gql, useApolloClient } from "@apollo/client";
import { Snackbar } from "@material-ui/core";
import { useAuthContext } from "auth/context";
import SlideTransitionComponent from "components/legacy/SlideTransitionComponent/SlideTransitionComponent";
import {
  SubmitFeedbackForStagePrisma,
  SubmitFeedbackForStagePrismaVariables,
} from "generated/schemaTypes";
import moment from "moment";
import { FunctionComponent, useEffect, useState } from "react";
import { analytics } from "react-hooks/useAnalytics";
import useMutation from "utils/useMutation";

import Controller from "./Controller";

const SUBMIT_FEEDBACK_PRISMA = gql`
  mutation SubmitFeedbackForStagePrisma(
    $input: SubmitCandidateFeedbackForStageInput!
  ) {
    submitCandidateFeedbackForStage(input: $input) {
      success
      candidateFeedback {
        id
        data
      }
    }
  }
`;

type FeedbackProps = {
  stageId: string;
  guideId: string;
  lastEventEndTime?: string;
  isDemo?: boolean;
  isSample?: boolean;
  previouslySubmittedFeedback?: boolean;
  previouslySubmittedTrustSurvey?: boolean;
  enableNPS?: boolean;
};

const Feedback: FunctionComponent<FeedbackProps> = (props) => {
  const {
    lastEventEndTime,
    stageId,
    guideId,
    previouslySubmittedFeedback,
    previouslySubmittedTrustSurvey,
    isDemo = false,
    isSample = false,
    enableNPS = true,
  } = props;

  const needsFeedback = !previouslySubmittedFeedback;

  const client = useApolloClient();
  const { user, loading: userLoading } = useAuthContext();

  const [submitFeedbackPrisma] = useMutation<
    SubmitFeedbackForStagePrisma,
    SubmitFeedbackForStagePrismaVariables
  >(SUBMIT_FEEDBACK_PRISMA);

  const isSignedIn = !!(!userLoading && user?.id);

  const hasLastInterviewEnded =
    lastEventEndTime && moment(lastEventEndTime).isBefore(moment(new Date()));
  const [isOpen, setOpen] = useState<boolean>(false);

  useEffect(() => {
    setOpen(
      (open) =>
        open || (!!hasLastInterviewEnded && !previouslySubmittedFeedback)
    );
  }, [
    hasLastInterviewEnded,
    previouslySubmittedFeedback,
    previouslySubmittedTrustSurvey,
  ]);
  const dismissFeedback = (): void => {
    analytics.track("Feedback Dismissed");
    // Refetch the main Guide query so the asterick goes away on the stage stepper
    client.refetchQueries({
      include: ["GuideForShortIdPrisma"],
    });
    setOpen(false);
  };
  if (isSample || isSignedIn) {
    return (
      <Controller
        isSample={isSample}
        dismissFeedback={(): void => {}}
        isDemo
        guideId={guideId}
        enableNPS={enableNPS}
        onSubmit={async () => {}}
        isGuideCandidate={false}
        needsFeedback
        showTrustSurvey={false}
      />
    );
  }

  return (
    <Snackbar
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      open={isOpen}
      TransitionComponent={SlideTransitionComponent}
    >
      <div>
        <Controller
          dismissFeedback={dismissFeedback}
          guideId={guideId}
          isDemo={isDemo}
          isSample={isSample}
          enableNPS={enableNPS}
          needsFeedback={needsFeedback}
          showTrustSurvey={false}
          isGuideCandidate={!isSignedIn}
          onSubmit={async ({ data: newData }) => {
            await submitFeedbackPrisma({
              variables: {
                input: {
                  stageId,
                  data: newData,
                },
              },
            });
          }}
        />
      </div>
    </Snackbar>
  );
};

export default Feedback;
